import React, { useState } from 'react';
import { Edit, Trash2, Calendar, User, FileText, MessageSquare, Layers } from 'lucide-react';
import { formatForDisplay, getRelativeTime, formatForDateTimeInput } from '../../../utils/timezoneUtils';
import { getStatusBadgeColor, getPriorityBadgeColor, handlePriorityChange } from './dashboardUtils';
import StatusDropdown from './StatusDropdown';
import PriorityDropdown from './PriorityDropdown';
import SimpleTaskList from './SimpleTaskList';
import SaveAsTemplateModal from './SaveAsTemplateModal';

const ProjectEditForm = ({ project, onSubmit, onCancel, isSubmitting }) => {
  const [formData, setFormData] = useState({
    name: project.name,
    description: project.description || '',
    status: project.status,
    priority: project.priority || 'medium',
    start_datetime: formatForDateTimeInput(project.start_datetime),
    end_datetime: formatForDateTimeInput(project.end_datetime)
  });
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) return;
    
    setError(null);
    try {
      await onSubmit(project.id, formData);
      onCancel();
    } catch (error) {
      console.error('Failed to update project:', error);
      setError(error.message || 'Failed to update project');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-slate-800 rounded-lg border border-slate-700 p-6 w-full max-w-lg mx-4">
        <h3 className="text-lg font-semibold text-white mb-4">Edit Project</h3>
        {error && (
          <div className="mb-4 p-3 bg-red-900/50 border border-red-700/50 rounded-lg">
            <p className="text-red-200 text-sm">{error}</p>
          </div>
        )}
        <form onSubmit={handleSubmit} className="space-y-4">
          <input
            type="text"
            placeholder="Project name..."
            value={formData.name}
            onChange={(e) => {
              setFormData({ ...formData, name: e.target.value });
              if (error) setError(null);
            }}
            className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-slate-200 placeholder-slate-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            required
          />
          <textarea
            placeholder="Description..."
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-slate-200 placeholder-slate-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 resize-none"
            rows="3"
          />
          <select
            value={formData.status}
            onChange={(e) => {
              setFormData({ ...formData, status: e.target.value });
              if (error) setError(null);
            }}
            className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-slate-200 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
          >
            <option value="not_started">Not Started</option>
            <option value="in_progress">In Progress</option>
            <option value="on_hold">On Hold</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <select
            value={formData.priority}
            onChange={(e) => {
              setFormData({ ...formData, priority: e.target.value });
              if (error) setError(null);
            }}
            className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-slate-200 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
          >
            <option value="low">Low Priority</option>
            <option value="medium">Medium Priority</option>
            <option value="high">High Priority</option>
            <option value="critical">Critical Priority</option>
          </select>
          <div className="grid grid-cols-2 gap-3">
            <input
              type="datetime-local"
              placeholder="Start date..."
              value={formData.start_datetime}
              onChange={(e) => setFormData({ ...formData, start_datetime: e.target.value })}
              className="px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-slate-200 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            />
            <input
              type="datetime-local"
              placeholder="End date..."
              value={formData.end_datetime}
              onChange={(e) => setFormData({ ...formData, end_datetime: e.target.value })}
              className="px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-slate-200 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            />
          </div>
          <div className="flex items-center space-x-3">
            <button
              type="submit"
              disabled={isSubmitting || !formData.name.trim()}
              className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-600 text-white px-4 py-2 rounded-lg transition-colors"
            >
              {isSubmitting ? 'Updating...' : 'Update Project'}
            </button>
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 text-slate-400 hover:text-white border border-slate-600 hover:border-slate-500 rounded-lg transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const NotesChatSidebar = ({ project, onAddNote }) => {
  const [newNote, setNewNote] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleAddNote = async (e) => {
    e.preventDefault();
    if (!newNote.trim()) return;

    setIsSubmitting(true);
    try {
      await onAddNote(project.id, newNote);
      setNewNote('');
    } catch (error) {
      console.error('Failed to add note:', error);
    }
    setIsSubmitting(false);
  };

  const notes = project.notes || [];

  return (
    <div className="w-72 xl:w-80 bg-slate-800 border-l border-slate-700 flex flex-col h-full">
      {/* Header */}
      <div className="p-3 xl:p-4 border-b border-slate-700">
        <div className="flex items-center space-x-2">
          <MessageSquare className="w-4 h-4 text-slate-400" />
          <h3 className="font-semibold text-white text-sm">Notes</h3>
          <span className="text-xs text-slate-400">({notes.length})</span>
        </div>
      </div>

      {/* Notes List - Scrollable */}
      <div className="flex-1 overflow-auto p-3 space-y-3 custom-scrollbar">
        {notes.length === 0 ? (
          <div className="text-center py-8">
            <MessageSquare className="w-8 h-8 text-slate-400 mx-auto mb-2" />
            <p className="text-slate-400 text-xs">No notes yet</p>
          </div>
        ) : (
          <>
            {notes.map((note) => (
              <div key={note.id} className="bg-slate-700/30 rounded-lg p-2">
                <p className="text-slate-200 text-sm mb-1">{note.content}</p>
                <div className="flex items-center justify-between text-xs text-slate-500">
                  <span className="font-medium">{note.created_by_name}</span>
                  <span>{new Date(note.created_at).toLocaleDateString('en-US', { 
                    month: 'short', 
                    day: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit'
                  })}</span>
                </div>
              </div>
            ))}
          </>
        )}
      </div>

      {/* Input - Sticky at bottom */}
      <div className="p-3 border-t border-slate-700">
        <form onSubmit={handleAddNote} className="space-y-2">
          <textarea
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            placeholder="Add a note..."
            className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-slate-200 placeholder-slate-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 resize-none text-sm"
            rows="2"
          />
          <button
            type="submit"
            disabled={isSubmitting || !newNote.trim()}
            className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-slate-600 text-white px-3 py-1.5 rounded transition-colors text-sm"
          >
            {isSubmitting ? 'Adding...' : 'Add Note'}
          </button>
        </form>
      </div>
    </div>
  );
};

const ProjectDetails = ({
  selectedProject,
  onUpdateProject,
  onDeleteProject,
  onCreateTask,
  onUpdateTask,
  onDeleteTask,
  onAddNote,
  onSaveAsTemplate,
  isSubmitting,
  showNotification,
  socket
}) => {
  const [showEditForm, setShowEditForm] = useState(false);
  const [showSaveTemplate, setShowSaveTemplate] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [isUpdatingPriority, setIsUpdatingPriority] = useState(false);

  if (!selectedProject) {
    return (
      <div className="flex-1 flex items-center justify-center bg-slate-900">
        <div className="text-center">
          <div className="w-16 h-16 bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-4">
            <FileText className="w-8 h-8 text-slate-400" />
          </div>
          <h3 className="text-lg font-medium text-slate-200 mb-2">Select a project</h3>
          <p className="text-slate-400">Choose a project from the list to view its details and tasks.</p>
        </div>
      </div>
    );
  }

  const handleDeleteProject = () => {
    if (window.confirm(`Delete project "${selectedProject.name}"? This will also delete all tasks and notes.`)) {
      onDeleteProject(selectedProject.id);
    }
  };

  const handleStatusChange = async (projectId, newStatus) => {
    setIsUpdatingStatus(true);
    try {
      await onUpdateProject(projectId, { 
        name: selectedProject.name,
        description: selectedProject.description || '',
        status: newStatus 
      });
    } catch (error) {
      showNotification('Failed to update project status', 'error');
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handlePriorityChangeLocal = async (projectId, newPriority) => {
    setIsUpdatingPriority(true);
    try {
      await handlePriorityChange(
        selectedProject,
        newPriority,
        onUpdateProject,
        null, // setSelectedProject not needed here since parent handles it
        null, // setShowPriorityDropdown not needed
        showNotification,
        socket
      );
    } catch (error) {
      showNotification('Failed to update project priority', 'error');
    } finally {
      setIsUpdatingPriority(false);
    }
  };

  const tasks = selectedProject.tasks || [];

  return (
    <div className="flex-1 bg-slate-900 flex flex-col lg:flex-row">
      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Project Header */}
        <div className="bg-slate-800 border-b border-slate-700 p-4 lg:p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1">
              <div className="flex items-center space-x-3 mb-2">
                <h1 className="text-2xl font-bold text-white">{selectedProject.name}</h1>
                <StatusDropdown
                  currentStatus={selectedProject.status}
                  onStatusChange={handleStatusChange}
                  projectId={selectedProject.id}
                  size="lg"
                  isUpdating={isUpdatingStatus}
                />
                <PriorityDropdown
                  currentPriority={selectedProject.priority}
                  onPriorityChange={handlePriorityChangeLocal}
                  projectId={selectedProject.id}
                  size="lg"
                  isUpdating={isUpdatingPriority}
                />
              </div>
              {selectedProject.description && (
                <p className="text-slate-300 mb-4">{selectedProject.description}</p>
              )}
            </div>
            <div className="flex items-center space-x-2">
              {onSaveAsTemplate && (
                <button
                  onClick={() => setShowSaveTemplate(true)}
                  className="p-2 text-slate-400 hover:text-blue-400 hover:bg-slate-700 rounded-lg transition-colors"
                  title="Save as template"
                >
                  <Layers className="w-4 h-4" />
                </button>
              )}
              <button
                onClick={() => setShowEditForm(true)}
                className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors"
                title="Edit project"
              >
                <Edit className="w-4 h-4" />
              </button>
              <button
                onClick={handleDeleteProject}
                className="p-2 text-slate-400 hover:text-red-400 hover:bg-slate-700 rounded-lg transition-colors"
                title="Delete project"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Project Meta Information */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-slate-400">
            <div className="flex items-center space-x-2">
              <User className="w-4 h-4" />
              <span>Created by {selectedProject.created_by_name}</span>
            </div>
            {selectedProject.start_datetime && (
              <div className="flex items-center space-x-2">
                <Calendar className="w-4 h-4" />
                <span>Start: {formatForDisplay(selectedProject.start_datetime)}</span>
              </div>
            )}
            {selectedProject.end_datetime && (
              <div className="flex items-center space-x-2">
                <Calendar className="w-4 h-4" />
                <span>Due: {formatForDisplay(selectedProject.end_datetime)} ({getRelativeTime(selectedProject.end_datetime)})</span>
              </div>
            )}
          </div>
        </div>

        {/* Tasks Section - Scrollable */}
        <div className="flex-1 overflow-auto p-4 lg:p-6 custom-scrollbar">
          <SimpleTaskList
            projectId={selectedProject.id}
            tasks={tasks}
            onCreateTask={onCreateTask}
            onUpdateTask={onUpdateTask}
            onDeleteTask={onDeleteTask}
            onUpdateProject={onUpdateProject}
            selectedProject={selectedProject}
            isSubmitting={isSubmitting}
          />
        </div>

        {/* Edit Form Modal */}
        {showEditForm && (
          <ProjectEditForm
            project={selectedProject}
            onSubmit={onUpdateProject}
            onCancel={() => setShowEditForm(false)}
            isSubmitting={isSubmitting}
          />
        )}

        {/* Save as Template Modal */}
        {onSaveAsTemplate && (
          <SaveAsTemplateModal
            isOpen={showSaveTemplate}
            onClose={() => setShowSaveTemplate(false)}
            sourceProject={selectedProject}
            onSubmit={(data) => onSaveAsTemplate(selectedProject.id, data)}
          />
        )}
      </div>

      {/* Notes Chat Sidebar */}
      <div className="lg:block hidden">
        <NotesChatSidebar
          project={selectedProject}
          onAddNote={onAddNote}
        />
      </div>
    </div>
  );
};

export default ProjectDetails;